import React, { useCallback, useEffect, useMemo, useState } from 'react';
// The base no-unused-vars rule does not see type-only uses.
// eslint-disable-next-line no-unused-vars
import { Box, Table, TableProps, TextFilter } from '@amzn/awsui-components-react-v3/polaris';
import { css, cx } from 'react-emotion';

/**
 * A table of every item in one scrolling list with a sticky header, an optional filter box, and
 * optional view and edit modes. Used by the Settings per-arc lists, upload previews, generated
 * rates, and change-set and impact tables.
 */

// The plain class name gives tests a stable hook; emotion supplies the style.
// The controls paint their own background, so the color goes on them and must beat Polaris.
export const EDITED_CELL_CLASS = 'edited-cell';
const EDITED_CELL = cx(
  EDITED_CELL_CLASS,
  css`
    & input,
    & button {
      background-color: #fff4d6 !important;
    }
  `
);

export interface ListColumn<T> {
  id: string;
  header: React.ReactNode;
  cell: (item: T) => React.ReactNode;
  // Replaces `cell` in edit mode; these cells are the ones that can be highlighted as edited.
  editCell?: (item: T) => React.ReactNode;
  // Shown only in edit mode.
  editOnly?: boolean;
}

type ListTableProps<T> = Omit<
  TableProps<T>,
  'items' | 'columnDefinitions' | 'pagination' | 'filter' | 'empty'
> & {
  items: T[];
  columns: ListColumn<T>[];
  // Shown when there are no items.
  empty?: string;
  editing?: boolean;
  isEdited?: (item: T, columnId: string) => boolean;
  // Adds a filter box keeping the items whose text contains the filter, ignoring case.
  filterText?: (item: T) => string;
  filteringPlaceholder?: string;
  // The filter, for a parent that must clear it; the table keeps its own when omitted.
  filtering?: { text: string; onChange: (text: string) => void };
  // Items shown whatever the filter, such as rows added while editing.
  alwaysShow?: (item: T) => boolean;
};

const centered = (text: string): JSX.Element => (
  <Box textAlign="center" color="inherit">
    {text}
  </Box>
);

/** Scrolls the element with the given id into view once the render it waits for has happened. */
export function useScrollIntoView(): (elementId: string) => void {
  // A new object each time, so asking for the same element twice scrolls twice.
  const [target, setTarget] = useState<{ elementId: string } | null>(null);
  useEffect(() => {
    const element = target && document.getElementById(target.elementId);
    if (element) {
      element.scrollIntoView({ block: 'center' });
    }
  }, [target]);
  return useCallback((elementId: string): void => setTarget({ elementId }), []);
}

function ListTable<T>({
  items,
  columns,
  empty,
  editing = false,
  isEdited,
  filterText,
  filteringPlaceholder,
  filtering,
  alwaysShow,
  ...tableProps
}: ListTableProps<T>): JSX.Element {
  const [ownFilteringText, setOwnFilteringText] = useState('');
  const filteringText = filtering ? filtering.text : ownFilteringText;
  const setFilteringText = filtering ? filtering.onChange : setOwnFilteringText;

  const shown = useMemo(() => {
    const needle = filteringText.trim().toLowerCase();
    if (!filterText || needle === '') {
      return items;
    }
    return items.filter(
      item =>
        (alwaysShow && alwaysShow(item)) ||
        filterText(item)
          .toLowerCase()
          .includes(needle)
    );
  }, [items, filterText, filteringText, alwaysShow]);

  const columnDefinitions = columns
    .filter(column => editing || !column.editOnly)
    .map(({ id, header, cell, editCell }) => {
      if (!editing || !editCell) {
        return { id, header, cell };
      }
      return {
        id,
        header,
        cell: (item: T): JSX.Element => (
          <div className={isEdited && isEdited(item, id) ? EDITED_CELL : undefined}>
            {editCell(item)}
          </div>
        ),
      };
    });

  const emptyText = items.length > 0 ? 'No matches.' : empty;

  return (
    <Table
      {...tableProps}
      items={shown}
      columnDefinitions={columnDefinitions}
      stickyHeader
      empty={emptyText && centered(emptyText)}
      filter={
        filterText && (
          <TextFilter
            filteringText={filteringText}
            filteringPlaceholder={filteringPlaceholder}
            filteringAriaLabel={filteringPlaceholder}
            countText={`${shown.length} ${shown.length === 1 ? 'match' : 'matches'}`}
            onChange={({ detail }): void => setFilteringText(detail.filteringText)}
          />
        )
      }
    />
  );
}

export default ListTable;
